import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { Download, Receipt } from '@mui/icons-material';
import apiService from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  receiptUrl: string | null;
  created: string;
  paid: boolean;
  organizationId: string;
}

interface TransactionListProps {
  organizationId: string;
}

const TransactionList: React.FC<TransactionListProps> = ({ organizationId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatDateShort } = useDateFormat();

  useEffect(() => {
    if (organizationId) {
      fetchTransactions();
    }
  }, [organizationId]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOrganizationTransactions(organizationId);
      setTransactions(data);
    } catch (err: any) {
      console.error('Failed to fetch transactions:', err);
      setError(err.response?.data?.error || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };


  const getStatusColor = (status: string, paid: boolean) => {
    if (paid && status === 'succeeded') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Receipt color="primary" />
            <Typography variant="h6">Transaction History</Typography>
          </Box>
        }
        subheader={`${transactions.length} transaction${transactions.length !== 1 ? 's' : ''} found`}
      />
      <CardContent>
        {transactions.length === 0 ? (
          <Alert severity="info">
            No transactions found for this organization.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateShort(transaction.created)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatAmount(transaction.amount, transaction.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={transaction.status}
                        color={getStatusColor(transaction.status, transaction.paid)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {transaction.receiptUrl ? (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          href={transaction.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionList;
